'use client';

import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import TaxResultCard from '../components/TaxResultCard';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

interface Message {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_calls?: any[];
  tool_call_id?: string;
}

interface McpTool {
  name: string;
  description: string;
}

interface TemplateSettings {
  enabled: boolean;
  instruction: string;
}

interface InputConditionSettings {
  enabled: boolean;
  autoConvertNetProfit: boolean;
  fiscalMonths: number;
  capitalClassification: 'under_10m' | '10m_to_100m' | 'over_100m';
}

export default function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mcpTools, setMcpTools] = useState<McpTool[]>([]);
  // const [showMcpPanel, setShowMcpPanel] = useState(false); // 削除済み
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [templateSettings, setTemplateSettings] = useState<TemplateSettings>({
    enabled: true,
    instruction: `【基本前提】

指定のない限り、以下の前提条件に基づき法人税計算を行う。
ただし、質問内で明示された条件・特例・地域設定がある場合は、そちらを優先する。

【法人区分と対象範囲】

資本金1億円以下または従業員300人以下の中小法人等として計算する。

大法人・連結納税グループは対象外。

外形標準課税は非該当（資本金1億円以下）。
※ただし、みなし大法人（親会社が大法人）の場合は地方税法上の適用可能性あり。

欠損金繰越控除は法人税法上の基本規定に基づく。
中小法人等は租税特別措置法上の80%制限の対象外（全額控除可）。

【交際費の取扱い】

中小法人は年800万円までの交際費等を全額損金算入可。

根拠：租税特別措置法（交際費等の損金算入特例に関する規定）
※条文番号の明示は不要。必要に応じて「租税特別措置法（交際費に関する規定）」と記す。

旧制度（30%損金算入方式、業種別差）は使用禁止。

「1人あたり5,000円以下の飲食費」等の細目は現行法に準拠して適用可否を判断。

【法人都民税（東京都）】

法人税割：7.0%（標準税率）

均等割：70,000円（資本金10,000,000円超〜100,000,000円以下）

23区所在法人：都民税のみ課税

多摩地区法人：都民税＋市町村民税を併課

複数事務所ありの場合：従業者数・給与支払額により按分計算

【法人事業税（東京都）】

所得400万円以下：3.5%

所得400万円超800万円以下：5.3%

所得800万円超：6.7%

外形標準課税：非該当（資本金1億円以下）

東京都按分様式を使用

地方法人特別税は廃止済み。現行では地方法人特別譲与税が自動反映される。

青色申告法人は予定申告額軽減の特例適用可能。

【青色申告承認法人】

所得8,000,000円以下部分：15%

8,000,000円超部分：23.2%

欠損金の繰越控除：全額控除可（中小法人のため80%制限非適用）

雇用促進税制・環境関連税制などの税額控除制度は対象

個人用語「青色申告特別控除」は使用禁止。

【電子申告（eLTAX対応）】

東京都では資本金1億円以下でも電子申告義務対象。

出力時は「eLTAXでXML送信される項目」（別表位置、チェック欄等）を1行補足する。

【出力・計算ルール】

「税額は？」等の質問には、
法人税・住民税（都民税）・事業税の3税すべてを計算して回答。

各税目の内訳＋合計額を明示。

端数処理：円未満切捨てを税目ごとに独立して行う。

税率・金額は単位（円・万円）を明示し、桁誤認を防ぐ。

段階別税率（事業税400/800万円区分）は小計＋合計を併記し、検算を行う。

旧制度・誤用例（交際費30%、全額15%、欠損金80%制限）を自動検出し、比較差額を併記する。

【法令・根拠の表記方法（安全化版）】

法令名＋論点名を記す。条文番号は原則省略。
例：「租税特別措置法（交際費に関する規定）」
　　「法人税法（減価償却に関する規定）」

条文番号を記載する場合は、
「令和6年度改正版に基づく推定条番号」である旨を付記。
（例：「租税特別措置法第61条の4【2024年度改正版】」）

条文が複数存在または改正予定の場合は、
「該当条文の確認を要す」と注記する。

【補足出力ルール】

均等割は課税所得0円でも課税（都民税70,000円）

法令引用・税率特例を使う際は、必ず法令名を括弧付きで示す。

「中小法人等」「中小企業者」など用語が異なる場合は両者を併記。

翌期繰越欠損金や未使用控除額は0円でも明示。

計算結果の下には、「この計算は円未満切捨て」と明記。

実務補足（別表位置、eLTAX送信設定）を1行で追記。

【優先順位ルール】

このテンプレートより質問内の指定条件を優先。

法令整合性 ＞ 出力の簡潔さ ＞ 数値の丸め順序 の順で重視。

条文番号を誤って生成するより、「法令カテゴリ＋要旨」で正確性を保つことを優先。

【目的】

東京都の中小法人を前提に、現行法（令和6年度改正後）に基づいた3税算出を自動化。

法令名は「カテゴリレベル（法＋論点）」で扱い、
誤った条文番号を自動生成しない。

eLTAX出力や別表の位置を補足して実務使用に耐えうる構成を保つ。`
  });
  const [inputConditionSettings, setInputConditionSettings] = useState<InputConditionSettings>({
    enabled: true,
    autoConvertNetProfit: true,
    fiscalMonths: 12,
    capitalClassification: 'under_10m',
  });
  const [activeTab, setActiveTab] = useState<'basic' | 'conditions'>('basic');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // HTMLタグをMarkdownに近い形へ正規化（特に改行の扱い）
  const normalizeAssistantContent = (text: string): string => {
    if (!text) return text;
    let t = text
      // <br>, <br/>, <br /> をMarkdownの改行に
      .replace(/<br\s*\/?\s*>/gi, '  \n')
      // 段落タグは空行に
      .replace(/<\/?p\s*>/gi, '\n\n')
      // divは改行に
      .replace(/<\/?div\s*>/gi, '\n')
      // ノーブレークスペースを通常スペースへ
      .replace(/&nbsp;/g, ' ');

    // LaTeX数式のデリミタを正規化（KaTeXでレンダリングさせる）
    // \[ ... \] -> $$ ... $$（ディスプレイ数式）
    t = t.replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_m, inner) => `$$${inner}$$`);
    // \( ... \) -> $ ... $（インライン数式）
    t = t.replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_m, inner) => `$${inner}$`);
    // 角括弧で囲まれ、内部がLaTeXっぽいブロックは $$...$$ に変換
    t = t.replace(/\[\s*([\s\S]*?)\s*\]/g, (m, inner) => {
      return (/\\[a-z]+|\\frac|\\text|\\times|\\mathbf|\\cdot|\\left|\\right|[\\^_]/.test(inner))
        ? `$$${inner}$$`
        : m;
    });

    return t;
  };

  useEffect(() => {
    // MCP機能一覧を取得
    fetchMcpTools();
    // テンプレート設定を読み込み
    loadTemplateSettings();
    // 入力条件設定を読み込み
    loadInputConditionSettings();
  }, []);

  const loadTemplateSettings = () => {
    try {
      const saved = localStorage.getItem('taxmcp-template-settings');
      if (saved) {
        const settings = JSON.parse(saved);
        setTemplateSettings(settings);
      }
    } catch (error) {
      console.error('Failed to load template settings:', error);
    }
  };

  const saveTemplateSettings = (settings: TemplateSettings) => {
    try {
      localStorage.setItem('taxmcp-template-settings', JSON.stringify(settings));
      setTemplateSettings(settings);
    } catch (error) {
      console.error('Failed to save template settings:', error);
    }
  };

  const loadInputConditionSettings = () => {
    try {
      const saved = localStorage.getItem('taxmcp-input-conditions');
      if (saved) {
        const settings = JSON.parse(saved);
        setInputConditionSettings(settings);
      }
    } catch (error) {
      console.error('Failed to load input condition settings:', error);
    }
  };

  const saveInputConditionSettings = (settings: InputConditionSettings) => {
    try {
      localStorage.setItem('taxmcp-input-conditions', JSON.stringify(settings));
      setInputConditionSettings(settings);
    } catch (error) {
      console.error('Failed to save input condition settings:', error);
    }
  };

  const fetchMcpTools = async () => {
    try {
      const response = await fetch('/api/mcp');
      if (response.ok) {
        const data = await response.json();
        if (data.result && data.result.tools) {
          setMcpTools(data.result.tools);
        }
      }
    } catch (error) {
      console.error('Failed to fetch MCP tools:', error);
    }
  };

  const formatMcpResult = (toolName: string, result: any): string => {
    if (!result || !result.content) {
      return 'MCP機能の実行結果を取得できませんでした。';
    }

    const content = result.content[0];
    if (!content || !content.text) {
      return 'MCP機能の実行結果を取得できませんでした。';
    }

    // JSONレスポンスをパースして人間が読みやすい形式に変換
    try {
      const parsedResult = JSON.parse(content.text);
      
      if (toolName === 'calculate_income_tax') {
        return `所得税計算結果:\n\n年収: ${parsedResult.annual_income?.toLocaleString()}円\n所得税額: ${parsedResult.income_tax?.toLocaleString()}円\n実効税率: ${parsedResult.effective_rate}%\n\n計算詳細:\n- 課税所得: ${parsedResult.taxable_income?.toLocaleString()}円\n- 基礎控除: ${parsedResult.basic_deduction?.toLocaleString()}円\n- その他控除: ${parsedResult.other_deductions?.toLocaleString()}円`.replace(/<br>/gi, '  \n');
      } else if (toolName === 'calculate_corporate_tax') {
        return `法人税計算結果:\n\n年間所得: ${parsedResult.annual_income?.toLocaleString()}円\n法人税額: ${parsedResult.corporate_tax?.toLocaleString()}円\n実効税率: ${parsedResult.effective_rate}%\n\n適用税率:\n- 基本税率: ${parsedResult.basic_rate}%\n- 軽減税率: ${parsedResult.reduced_rate}%`.replace(/<br>/gi, '  \n');
      } else if (toolName === 'calculate_resident_tax') {
        return `住民税計算結果:\n\n課税所得: ${parsedResult.taxable_income?.toLocaleString()}円\n住民税額: ${parsedResult.resident_tax?.toLocaleString()}円\n\n内訳:\n- 所得割: ${parsedResult.income_based?.toLocaleString()}円\n- 均等割: ${parsedResult.flat_rate?.toLocaleString()}円`.replace(/<br>/gi, '  \n');
      } else if (toolName === 'calculate_enhanced_corporate_tax') {
        return `詳細法人税計算結果:\n\n会計利益: ${parsedResult.accounting_profit?.toLocaleString()}円\n課税所得: ${parsedResult.taxable_income?.toLocaleString()}円\n法人税額: ${parsedResult.corporate_tax?.toLocaleString()}円\n実効税率: ${parsedResult.effective_rate}%\n\n調整項目:\n- 加算項目: ${parsedResult.additions?.toLocaleString()}円\n- 減算項目: ${parsedResult.deductions?.toLocaleString()}円`.replace(/<br>/gi, '  \n');
      } else if (toolName === 'search_legal_reference') {
        const references = parsedResult.references || [];
        let result = `法的参照検索結果:\n\n検索クエリ: "${parsedResult.query}"\n\n`;
        references.forEach((ref: any, index: number) => {
          result += `${index + 1}. ${ref.title}\n   ${ref.description}\n   参照: ${ref.reference}\n\n`;
        });
        return result.replace(/<br>/gi, '  \n');
      }
      
      // その他のツールの場合はJSONを整形して表示
      return JSON.stringify(parsedResult, null, 2);
    } catch (e) {
      // JSONパースに失敗した場合は元のテキストを返す
      return content.text.replace(/<br>/gi, '  \n');
    }
  };

  const callMcpFunction = async (toolName: string, params: any = {}) => {
    setIsLoading(true);
    
    const userMessage: Message = { 
      role: 'user', 
      content: `税務計算機能「${toolName}」を実行中...` 
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await fetch('/api/mcp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: `tools/call`,
          params: {
            name: toolName,
            arguments: params
          }
        }),
      });

      if (!response.ok) {
        throw new Error('MCP function call failed');
      }

      const data = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: normalizeAssistantContent(formatMcpResult(toolName, data.result)),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling MCP function:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'MCP機能の呼び出しに失敗しました。',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeSendMessage = async (messageText: string, skipSettings: boolean = false) => {
    if (!messageText.trim() || isLoading) return;

    // 入力条件設定を適用してメッセージを処理
    let processedInput = messageText;
    if (!skipSettings && inputConditionSettings.enabled) {
      // 当期純利益の自動読み替え
      if (inputConditionSettings.autoConvertNetProfit) {
        processedInput = processedInput.replace(/当期純利益/g, '税引前当期利益');
      }
      
      // 事業年度月数の自動補完（「○ヶ月」の記載がない場合）
      if (!processedInput.includes('ヶ月') && !processedInput.includes('か月') && !processedInput.includes('カ月')) {
        if (inputConditionSettings.fiscalMonths !== 12) {
          processedInput += `（事業年度：${inputConditionSettings.fiscalMonths}ヶ月）`;
        }
      }
      
      // 資本金分類の自動補完（資本金の記載がない場合）
      if (!processedInput.includes('資本金') && !processedInput.includes('資本')) {
        const capitalText = {
          'under_10m': '資本金1000万円以下',
          '10m_to_100m': '資本金1000万円超1億円以下', 
          'over_100m': '資本金1億円超'
        }[inputConditionSettings.capitalClassification];
        processedInput += `（${capitalText}）`;
      }
    }

    // テンプレート設定が有効な場合、共通指示を追加
    let messageContent = processedInput;
    if (!skipSettings && templateSettings.enabled && templateSettings.instruction.trim()) {
      const cleanedInstruction = templateSettings.instruction.replace(/<BR>/gi, '\n');
      messageContent = `${cleanedInstruction}\n\n${processedInput}`;
    }

    const userMessage: Message = { role: 'user', content: messageText }; // 表示用は元のメッセージ
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    // APIには処理済みメッセージを送信
    const messagesForApi = [...messages, { role: 'user' as const, content: messageContent }];

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: messagesForApi }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      if (data.messages && Array.isArray(data.messages)) {
        const receivedMessages: Message[] = data.messages.map((msg: any) => ({
          role: msg.role,
          content: msg.content ? normalizeAssistantContent(msg.content) : '',
          tool_calls: msg.tool_calls,
          tool_call_id: msg.tool_call_id
        }));
        setMessages([...newMessages, ...receivedMessages]);
      } else {
        const assistantMessage: Message = {
          role: 'assistant',
          content: normalizeAssistantContent(data.message || ''),
        };
        setMessages([...newMessages, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'エラーが発生しました。もう一度お試しください。',
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = () => executeSendMessage(input);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const advancedSamples = [
    {
      title: "法人事業税・地方法人特別税",
      query: "売上高が 7,800万円、経常利益が 450万円 の法人ですが、別表五（二）で「地方税損金算入」の仕訳を入力する際、東京都の 外形標準課税 は適用されますか？資本金が 1,000万円未満 で従業員数が 10名 の場合、均等割だけで良いのか、所得割も別表六（一）に入力する必要があるか迷っています。"
    },
    {
      title: "法人都民税の均等割",
      query: "本店（新宿区）と支店（立川市）の2か所に事務所があります。資本金が 5,000万円、従業員数が 40人（うち支店20人） の場合、別表六（一）に入力する「均等割額」は、東京都全体で 235,000円 か、事業所ごとに按分して入力する必要がありますか？また、支店分を「事業所税」の別表に重複計上していませんか？"
    },
    {
      title: "税額控除関連",
      query: "研究開発費が 200万円 あり、試験研究費税額控除を適用予定です。別表六（一）および別表七（一）に入力する際、控除率は東京都の地方法人税にも同率で適用されますか？また、控除後の地方法人税の再計算を別表十六のどこに反映すれば良いでしょうか。"
    },
    {
      title: "外形標準課税の計算",
      query: "資本金が 1億2,000万円、東京都内の本店で営業しており、営業収益が 3億円、人件費が 6,000万円、賃借料が 1,200万円 の場合、別表十五における「付加価値割」と「資本割」の計算をどう記載すべきですか？Excelで計算したところ、東京都の課税標準額が 1,800万円 となりましたが、別表十五（ニ）での「按分率」を0.75で入力するのは正しいですか？"
    },
    {
      title: "中小企業向け軽減税率",
      query: "売上高 6,500万円、資本金 8,000万円 の東京都内法人です。別表六（一）で法人税率を15%に軽減して計算したところ、e-Taxで「中小法人軽減税率の適用対象外」とエラーが出ます。東京都の「地方法人税率」部分も同時に修正すべきでしょうか？また、別表二十（四）の「資本金等の額」を8,000万円で記入して良いですか？"
    },
    {
      title: "赤字繰越控除",
      query: "前期の欠損金が △2,300,000円、当期利益が 1,200,000円 です。東京都の地方法人税別表六（一）で「所得金額」を控除後に減額しましたが、別表七（一）の控除限度額（50%制限）の計算結果と不一致になります。東京都の別表六（一）での記入は国税と同一欄ですか？"
    },
    {
      title: "予定申告額の処理",
      query: "東京都からの法人事業税の予定申告分（120,000円）を前期に支払いました。別表五（二）で損金算入していますが、確定額（135,000円）との差額を当期にどのように修正仕訳し、別表五（二）の「未払法人税等」へ反映すべきですか？"
    },
    {
      title: "交際費の損金算入限度額",
      query: "売上高が 7,500万円、交際費が 480,000円 でした。別表十五で交際費の限度額を算出したところ、年800万円の範囲内なので全額損金としました。ただし東京都の地方法人税では、交際費損金不算入部分を再加算すべきと聞きました。別表四の「加算」欄に入力が必要ですか？"
    },
    {
      title: "課税事業者への切替",
      query: "2024年度は免税事業者、2025年度から課税事業者へ変更しました。売上が 8,200万円 を超えたためですが、東京都の別表五（二）で「仮払消費税等」をどの時点で損金算入に戻すか迷っています。地方法人税・法人住民税ではこの処理は影響しませんか？"
    },
    {
      title: "事業所税の申告",
      query: "港区のオフィス面積が 320㎡、従業員数が 28名、資本金が 3,000万円 です。東京都事業所税の「従業者割」「資産割」の対象となる場合、別表一七の「租税公課」に東京都事業所税分を含めて良いですか？また、電子申告で「地方法人税」の付表に自動連動されないのはなぜですか？"
    }
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">AIチャット + MCP税務計算</h1>
            <p className="text-sm text-gray-600 mt-1">税務に関する質問をすると自動的にMCP機能が呼び出されます</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setShowTemplatePanel(!showTemplatePanel)}
              className={`hidden px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                templateSettings.enabled
                  ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
                  : 'bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-gray-500'
              }`}
            >
              {showTemplatePanel ? 'テンプレート設定を隠す' : 'テンプレート設定'}
            </button>
          </div>
        </div>
      </div>

      {/* Template Settings Panel */}
      {showTemplatePanel && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <h2 className="text-lg font-semibold text-blue-800 mb-2">テンプレート設定</h2>
          <p className="text-sm text-blue-700 mb-3">すべてのメッセージに自動的に追加される共通指示を設定できます</p>
          
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-4 py-2 rounded-t-lg ${activeTab === 'basic' ? 'bg-white text-blue-800 border-b-2 border-blue-500' : 'bg-blue-100 text-blue-600'}`}
            >
              基本テンプレート
            </button>
            <button
              onClick={() => setActiveTab('conditions')}
              className={`px-4 py-2 rounded-t-lg ${activeTab === 'conditions' ? 'bg-white text-blue-800 border-b-2 border-blue-500' : 'bg-blue-100 text-blue-600'}`}
            >
              入力条件設定
            </button>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-200">
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={templateSettings.enabled}
                      onChange={(e) => saveTemplateSettings({ ...templateSettings, enabled: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-800">テンプレート機能を有効にする</span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    共通指示（すべてのメッセージの前に自動追加されます）
                  </label>
                  <textarea
                    value={templateSettings.instruction}
                    onChange={(e) => saveTemplateSettings({ ...templateSettings, instruction: e.target.value })}
                    placeholder="例: 指定の無い限り、法人税の計算として扱うこと"
                    className="w-full p-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    disabled={!templateSettings.enabled}
                  />
                </div>
                
                <div className="bg-blue-100 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-1">プレビュー:</p>
                  <p className="text-sm text-blue-700">
                    {templateSettings.enabled && templateSettings.instruction.trim()
                      ? `"${templateSettings.instruction}" + あなたのメッセージ`
                      : 'あなたのメッセージのみ（テンプレート無効）'
                    }
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'conditions' && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputConditionSettings.enabled}
                      onChange={(e) => saveInputConditionSettings({ ...inputConditionSettings, enabled: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-800">入力条件設定を有効にする</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    当期純利益の自動読み替え（税引前当期利益として扱う）
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputConditionSettings.autoConvertNetProfit}
                      onChange={(e) => saveInputConditionSettings({ ...inputConditionSettings, autoConvertNetProfit: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      disabled={!inputConditionSettings.enabled}
                    />
                    <span className="text-sm text-blue-800">有効</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    事業年度月数（デフォルト値）
                  </label>
                  <input
                    type="number"
                    value={inputConditionSettings.fiscalMonths}
                    onChange={(e) => saveInputConditionSettings({ ...inputConditionSettings, fiscalMonths: Math.min(12, Math.max(1, parseInt(e.target.value) || 12)) })}
                    min={1}
                    max={12}
                    className="w-full p-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!inputConditionSettings.enabled}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    資本金分類（デフォルト）
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={inputConditionSettings.capitalClassification === 'under_10m'}
                        onChange={() => saveInputConditionSettings({ ...inputConditionSettings, capitalClassification: 'under_10m' })}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={!inputConditionSettings.enabled}
                      />
                      <span className="text-sm text-blue-800">1000万円以下</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={inputConditionSettings.capitalClassification === '10m_to_100m'}
                        onChange={() => saveInputConditionSettings({ ...inputConditionSettings, capitalClassification: '10m_to_100m' })}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={!inputConditionSettings.enabled}
                      />
                      <span className="text-sm text-blue-800">1000万円～1億円</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={inputConditionSettings.capitalClassification === 'over_100m'}
                        onChange={() => saveInputConditionSettings({ ...inputConditionSettings, capitalClassification: 'over_100m' })}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={!inputConditionSettings.enabled}
                      />
                      <span className="text-sm text-blue-800">1億円以上</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MCP Panel は削除済み（将来必要に応じて復元可能） */}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-20">
            <p className="text-lg">チャットを開始してください</p>
            <p className="text-sm mt-2">税務に関する質問をすると自動的にMCP機能が呼び出されます</p>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg max-w-md mx-auto">
              <p className="text-sm text-blue-800 font-medium mb-2">試してみてください:</p>
              <ul className="text-sm text-blue-700 space-y-1 text-left">
                <li 
                  className="cursor-pointer hover:underline hover:text-blue-900"
                  onClick={() => executeSendMessage('年間所得1000万円の法人税を計算して')}
                >
                  年間所得1000万円の法人税を計算して
                </li>
                <li 
                  className="cursor-pointer hover:underline hover:text-blue-900"
                  onClick={() => executeSendMessage('会計利益1500万円、減価償却超過額200万円、交際費損金不算入50万円の場合の法人税額は？')}
                >
                  会計利益1500万円、減価償却超過額200万円、交際費損金不算入50万円の場合の法人税額は？
                </li>
              </ul>
              {templateSettings.enabled && (
                <div className="hidden mt-3 p-2 bg-yellow-100 rounded border border-yellow-300">
                  <p className="text-xs text-yellow-800 font-medium">テンプレート有効:</p>
                  <p className="text-xs text-yellow-700 mt-1">"{templateSettings.instruction}" が自動追加されます</p>
                </div>
              )}
            </div>

            <div className="mt-6 max-w-4xl mx-auto px-4">
              <p className="text-sm text-blue-800 font-medium mb-3 text-left">さらに詳しく試したいとき:</p>
              <div className="flex overflow-x-auto space-x-4 pb-4 scrollbar-thin scrollbar-thumb-blue-200 scrollbar-track-transparent">
                {advancedSamples.map((sample, index) => (
                  <div 
                    key={index} 
                    className="flex-none w-72 p-4 bg-white rounded-lg border border-blue-100 shadow-sm flex flex-col hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-sm font-bold text-blue-900 mb-2 truncate" title={sample.title}>
                      {sample.title}
                    </h3>
                    <p className="text-xs text-gray-600 mb-4 flex-grow line-clamp-3 text-left leading-relaxed">
                      {sample.query}
                    </p>
                    <button
                      onClick={() => executeSendMessage(sample.query, true)}
                      className="mt-auto w-full py-2 bg-blue-50 text-blue-600 text-xs font-medium rounded hover:bg-blue-100 transition-colors flex items-center justify-center"
                    >
                      <span className="mr-1">▶</span> 実行する
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {messages.filter(m => m.content).map((message, index) => (
          <div
            key={index}
            className={`flex ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-3xl px-4 py-2 rounded-lg ${
                message.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              {message.role === 'tool' ? (
                <div className="w-full flex justify-center">
                  <TaxResultCard data={message.content} toolName={message.name || ''} />
                </div>
              ) : message.role === 'assistant' ? (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{
                      // Math display components
                      div: ({node, className, children, ...props}: any) => {
                        if (className === 'math math-display') {
                          return <div className="math-display my-4 text-center" {...props}>{children}</div>;
                        }
                        return <div {...props}>{children}</div>;
                      },
                      span: ({node, className, children, ...props}: any) => {
                        if (className === 'math math-inline') {
                          return <span className="math-inline" {...props}>{children}</span>;
                        }
                        return <span {...props}>{children}</span>;
                      },
                      h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-lg font-semibold mb-2" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-md font-semibold mb-1" {...props} />,
                      p: ({node, ...props}) => <p className="mb-2" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" {...props} />,
                      li: ({node, ...props}) => <li className="mb-1" {...props} />,
                      code: ({node, inline, className, children, ...props}: any) => {
                        const match = /language-(\w+)/.exec(className || '');
                        const isInline = inline || !match;
                        return isInline ? (
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>
                            {children}
                          </code>
                        ) : (
                          <code className="block bg-gray-100 p-2 rounded text-sm overflow-x-auto" {...props}>
                            {children}
                          </code>
                        );
                      },
                      pre: ({node, ...props}) => <pre className="bg-gray-100 p-2 rounded overflow-x-auto mb-2" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-2" {...props} />,
                      table: ({node, ...props}) => <table className="border-collapse border border-gray-300 mb-2" {...props} />,
                      th: ({node, ...props}) => <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-semibold" {...props} />,
                      td: ({node, ...props}) => <td className="border border-gray-300 px-2 py-1" {...props} />,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{message.content}</div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-3xl px-4 py-2 rounded-lg bg-white text-gray-800 border border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>考え中...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex space-x-4">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="メッセージを入力してください..."
              className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              送信
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
